/**
 * useAccountSearchModal — extracted state + effects + handlers from AccountSearchModal.
 * Keeps the component file under the function size cap; behaviour unchanged.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'

import { type Party } from '@/gen/proto'

import { filterAccounts, groupAccounts } from '../../pages/journal-entry-form/journal-entry-form-helpers'
import { type Account } from '../../services/account-service'
import { listAllParties, listPartyRoleAccounts, ensurePartyAccount, type PartyRoleAccountItem } from '../../services/party-service'

export interface AccountSearchModalState {
  // Common
  activeMode: 'account' | 'party'
  setActiveMode: (m: 'account' | 'party') => void
  search: string
  setSearch: (s: string) => void
  inputRef: React.RefObject<HTMLInputElement>
  listRef: React.RefObject<HTMLDivElement>
  containerRef: React.RefObject<HTMLDivElement>
  // Account list
  selectedIndex: number
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>
  filteredAccounts: Account[]
  groupedAccounts: ReturnType<typeof groupAccounts>
  flatAccounts: Account[]
  displayAccounts: Account[]
  // Party step 1
  parties: Party[]
  isLoadingParties: boolean
  partySelectedIndex: number
  setPartySelectedIndex: React.Dispatch<React.SetStateAction<number>>
  isAddPartyOpen: boolean
  setIsAddPartyOpen: (b: boolean) => void
  bumpPartyListVersion: () => void
  // Party step 2
  selectedParty: Party | null
  setSelectedParty: (p: Party | null) => void
  roleAccounts: PartyRoleAccountItem[]
  isLoadingRoles: boolean
  roleSelectedIndex: number
  setRoleSelectedIndex: React.Dispatch<React.SetStateAction<number>>
  creatingIndex: number | null
  roleLoadError: string | null
  // Actions
  pickParty: (party: Party) => void
  goBackToPartySearch: () => void
  selectRoleAccount: (item: PartyRoleAccountItem, index: number) => Promise<void>
  switchToMode: (m: 'account' | 'party') => void
  handleBlur: (e: React.FocusEvent) => void
}

interface UseAccountSearchModalArgs {
  isOpen: boolean
  onClose: () => void
  accounts: Account[]
  initialSearch: string
  initialMode: 'account' | 'party'
  organizationId: string
  partyRoleFilter?: string
  onSelectParty?: (party: Party, role: string, purpose: string, accountId: string) => void
}

function useResetOnOpen(args: {
  isOpen: boolean; initialMode: 'account' | 'party'; initialSearch: string
  setActiveMode: (m: 'account' | 'party') => void
  setSearch: (s: string) => void
  setSelectedIndex: (n: number) => void
  setPartySelectedIndex: (n: number) => void
  setParties: (p: Party[]) => void
  setSelectedParty: (p: Party | null) => void
  setRoleAccounts: (r: PartyRoleAccountItem[]) => void
  setRoleSelectedIndex: (n: number) => void
  setCreatingIndex: (n: number | null) => void
  setRoleLoadError: (s: string | null) => void
  setIsAddPartyOpen: (b: boolean) => void
  inputRef: React.RefObject<HTMLInputElement>
}): void {
  useEffect(() => {
    if (!args.isOpen) { return }
    args.setActiveMode(args.initialMode)
    args.setSearch(args.initialSearch)
    args.setSelectedIndex(0)
    args.setPartySelectedIndex(0)
    args.setParties([])
    args.setSelectedParty(null)
    args.setRoleAccounts([])
    args.setRoleSelectedIndex(0)
    args.setCreatingIndex(null)
    args.setRoleLoadError(null)
    args.setIsAddPartyOpen(false)
    setTimeout(() => args.inputRef.current?.focus(), 50)
  }, [args.isOpen, args.initialSearch, args.initialMode]) // eslint-disable-line react-hooks/exhaustive-deps
}

interface ScrollIntoViewArgs {
  activeMode: 'account' | 'party'
  selectedParty: Party | null
  listRef: React.RefObject<HTMLDivElement>
  displayAccounts: Account[]
  selectedIndex: number
  partySelectedIndex: number
  roleSelectedIndex: number
}

function useScrollIntoView(args: ScrollIntoViewArgs): void {
  const { activeMode, selectedParty, listRef, displayAccounts, selectedIndex, partySelectedIndex, roleSelectedIndex } = args
  useEffect(() => {
    if (activeMode !== 'account') { return }
    const el = listRef.current?.querySelector(`[data-account-id="${displayAccounts[selectedIndex]?.id}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex, displayAccounts, activeMode, listRef])
  useEffect(() => {
    if (activeMode !== 'party' || selectedParty) { return }
    const el = listRef.current?.querySelector(`[data-party-index="${partySelectedIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [partySelectedIndex, activeMode, selectedParty, listRef])
  useEffect(() => {
    if (!selectedParty) { return }
    const el = listRef.current?.querySelector(`[data-role-index="${roleSelectedIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [roleSelectedIndex, selectedParty, listRef])
}

interface UsePartyFetchArgs {
  isOpen: boolean
  activeMode: 'account' | 'party'
  selectedParty: Party | null
  search: string
  organizationId: string
  partyRoleFilter?: string
  partyListVersion: number
  setIsLoadingParties: (b: boolean) => void
  setParties: (p: Party[]) => void
}

function usePartyFetch(args: UsePartyFetchArgs): void {
  useEffect(() => {
    if (!args.isOpen || args.activeMode !== 'party' || args.selectedParty) { return }
    const trimmed = args.search.trim()
    args.setIsLoadingParties(true)
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const resp = await listAllParties({
            orgId: args.organizationId,
            filters: { query: trimmed || undefined, activeOnly: true, role: args.partyRoleFilter },
            pageSize: 30,
          })
          args.setParties(resp.data?.parties ?? [])
        } catch { /* ignore */ } finally { args.setIsLoadingParties(false) }
      })()
    }, trimmed ? 250 : 0)
    return () => clearTimeout(timer)
  }, [args.search, args.activeMode, args.isOpen, args.organizationId, args.selectedParty, args.partyRoleFilter, args.partyListVersion]) // eslint-disable-line react-hooks/exhaustive-deps
}

// eslint-disable-next-line max-lines-per-function -- composes all modal state; sub-hooks above
export function useAccountSearchModal(args: UseAccountSearchModalArgs): AccountSearchModalState {
  const { isOpen, onClose, accounts, initialSearch, initialMode, organizationId, partyRoleFilter, onSelectParty } = args

  const [activeMode, setActiveMode] = useState<'account' | 'party'>(initialMode)
  const [search, setSearch] = useState(initialSearch)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [parties, setParties] = useState<Party[]>([])
  const [isLoadingParties, setIsLoadingParties] = useState(false)
  const [partySelectedIndex, setPartySelectedIndex] = useState(0)
  const [isAddPartyOpen, setIsAddPartyOpen] = useState(false)
  const [partyListVersion, setPartyListVersion] = useState(0)

  const [selectedParty, setSelectedParty] = useState<Party | null>(null)
  const [roleAccounts, setRoleAccounts] = useState<PartyRoleAccountItem[]>([])
  const [isLoadingRoles, setIsLoadingRoles] = useState(false)
  const [roleSelectedIndex, setRoleSelectedIndex] = useState(0)
  const [creatingIndex, setCreatingIndex] = useState<number | null>(null)
  const [roleLoadError, setRoleLoadError] = useState<string | null>(null)

  const filteredAccounts = useMemo(() => filterAccounts(accounts, search), [accounts, search])
  const groupedAccounts = useMemo(() => groupAccounts(filteredAccounts), [filteredAccounts])
  const flatAccounts = useMemo(() => groupedAccounts.flatMap(g => g.accounts), [groupedAccounts])
  const displayAccounts = search ? filteredAccounts : flatAccounts

  useResetOnOpen({
    isOpen, initialMode, initialSearch,
    setActiveMode, setSearch, setSelectedIndex, setPartySelectedIndex,
    setParties, setSelectedParty, setRoleAccounts, setRoleSelectedIndex,
    setCreatingIndex, setRoleLoadError, setIsAddPartyOpen, inputRef,
  })

  useEffect(() => { setSelectedIndex(0) }, [search])
  useEffect(() => { if (!selectedParty) { setPartySelectedIndex(0) } }, [search, activeMode, selectedParty])

  useScrollIntoView({ activeMode, selectedParty, listRef, displayAccounts, selectedIndex, partySelectedIndex, roleSelectedIndex })
  usePartyFetch({
    isOpen, activeMode, selectedParty, search, organizationId, partyRoleFilter, partyListVersion,
    setIsLoadingParties, setParties,
  })

  useEffect(() => {
    if (!selectedParty) { return }
    setIsLoadingRoles(true)
    setRoleAccounts([])
    setRoleSelectedIndex(0)
    setRoleLoadError(null)
    listPartyRoleAccounts(organizationId, selectedParty.id)
      .then(resp => { setRoleAccounts(resp.data?.roleAccounts ?? []) })
      .catch((e: unknown) => { setRoleLoadError(e instanceof Error ? e.message : 'Failed to load accounts') })
      .finally(() => setIsLoadingRoles(false))
  }, [selectedParty, organizationId])

  const handleBlur = useCallback((e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as Node | null
    if (containerRef.current && relatedTarget && !containerRef.current.contains(relatedTarget)) { onClose() }
    if (!relatedTarget) {
      setTimeout(() => {
        if (containerRef.current && !containerRef.current.contains(document.activeElement)) { onClose() }
      }, 100)
    }
  }, [onClose])

  const pickParty = (party: Party): void => {
    setSelectedParty(party)
    setSearch('')
    setTimeout(() => inputRef.current?.focus(), 30)
  }

  const goBackToPartySearch = (): void => {
    setSelectedParty(null)
    setRoleAccounts([])
    setRoleLoadError(null)
    setSearch('')
    setTimeout(() => inputRef.current?.focus(), 30)
  }

  const selectRoleAccount = async (item: PartyRoleAccountItem, index: number): Promise<void> => {
    if (!selectedParty) { return }
    let accountId = item.accountId ?? ''
    if (!accountId) {
      setCreatingIndex(index)
      try {
        const resp = await ensurePartyAccount(organizationId, selectedParty.id, item.purpose, item.role)
        accountId = resp.data?.accountId ?? ''
        setRoleAccounts(prev => prev.map((ra, i) => i === index ? { ...ra, accountId } : ra))
      } catch { /* ignore — onSelectParty called with empty accountId, auto-create will retry */ }
      finally { setCreatingIndex(null) }
    }
    onSelectParty?.(selectedParty, item.role, item.purpose, accountId)
    onClose()
  }

  const switchToMode = useCallback((mode: 'account' | 'party'): void => {
    setActiveMode(mode)
    setSearch('')
    setSelectedParty(null)
    setRoleAccounts([])
    setTimeout(() => inputRef.current?.focus(), 30)
  }, [])

  return {
    activeMode, setActiveMode, search, setSearch, inputRef, listRef, containerRef,
    selectedIndex, setSelectedIndex, filteredAccounts, groupedAccounts, flatAccounts, displayAccounts,
    parties, isLoadingParties, partySelectedIndex, setPartySelectedIndex,
    isAddPartyOpen, setIsAddPartyOpen,
    bumpPartyListVersion: () => setPartyListVersion(v => v + 1),
    selectedParty, setSelectedParty, roleAccounts, isLoadingRoles,
    roleSelectedIndex, setRoleSelectedIndex, creatingIndex, roleLoadError,
    pickParty, goBackToPartySearch, selectRoleAccount, switchToMode, handleBlur,
  }
}
